import { useEffect, useState } from 'react';

const CHANNEL_NAME = 'copilot-popout';
export const POPOUT_PATH = '/copilot/popout';

export function isPopoutWindow(): boolean {
  return (
    typeof window !== 'undefined' && window.location.pathname === POPOUT_PATH
  );
}

type PopoutMessage =
  | { type: 'open' | 'close' }
  | { type: 'study'; studyUuid: string | null };

let popoutRef: Window | null = null;
let watchTimer: number | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(CHANNEL_NAME);
}

/** Initial open study read from the popout window's URL (`?study=<uuid>`), so it
 * has context immediately — before the first BroadcastChannel update arrives. */
export function readInitialPopoutStudy(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('study');
}

// Reused across the frequent active-study updates instead of create/close per call.
let publishChannel: BroadcastChannel | null = null;

/** Main window → popout: broadcast the currently-open study so the popout's
 * messages carry study context (it has no router/`CopilotStudySync`). */
export function publishActiveStudy(studyUuid: string | null): void {
  if (typeof BroadcastChannel === 'undefined') return;
  if (!publishChannel) publishChannel = new BroadcastChannel(CHANNEL_NAME);
  publishChannel.postMessage({ type: 'study', studyUuid } as PopoutMessage);
}

/** Popout: subscribe to active-study changes from the main window. */
export function subscribeActiveStudy(
  cb: (studyUuid: string | null) => void
): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const ch = new BroadcastChannel(CHANNEL_NAME);
  ch.onmessage = (ev: MessageEvent<PopoutMessage>) => {
    if (ev.data?.type === 'study') cb(ev.data.studyUuid);
  };
  return () => ch.close();
}

export function openCopilotPopout(studyUuid?: string | null): void {
  if (popoutRef && !popoutRef.closed) {
    popoutRef.focus();
    return;
  }
  const query = studyUuid ? `?study=${encodeURIComponent(studyUuid)}` : '';
  const url = `${window.location.origin}${POPOUT_PATH}${query}`;
  const features =
    'popup=yes,width=440,height=720,menubar=no,toolbar=no,location=no,status=no';
  popoutRef = window.open(url, 'copilot-popout', features);
  if (!popoutRef) return;

  const ch = getChannel();
  ch?.postMessage({ type: 'open' } as PopoutMessage);
  ch?.close();

  if (watchTimer) window.clearInterval(watchTimer);
  watchTimer = window.setInterval(() => {
    if (popoutRef && popoutRef.closed) {
      const c = getChannel();
      c?.postMessage({ type: 'close' } as PopoutMessage);
      c?.close();
      popoutRef = null;
      if (watchTimer) {
        window.clearInterval(watchTimer);
        watchTimer = null;
      }
    }
  }, 500);
}

/**
 * Subscribe to popout visibility changes. Returns true while a popout is open.
 * Meant for the main window's widget so it can hide itself.
 */
export function usePopoutVisibility(): boolean {
  const [open, setOpen] = useState(() => isPopoutWindow());

  useEffect(() => {
    if (isPopoutWindow()) {
      setOpen(true);
      return undefined;
    }
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.onmessage = (ev: MessageEvent<PopoutMessage>) => {
      if (ev.data?.type === 'open') setOpen(true);
      if (ev.data?.type === 'close') setOpen(false);
    };
    return () => ch.close();
  }, []);

  return open;
}

/** Called from the popout window to announce open/close to the main window. */
export function announcePopout(type: 'open' | 'close'): void {
  const ch = getChannel();
  ch?.postMessage({ type } as PopoutMessage);
  ch?.close();
}
